import React, { useState, useEffect, useRef } from "react";
import { PageLayout } from "../../components/page-layout";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { OnboardingProgressBar } from "../../components/onboarding-progress-bar";
import { useOnboarding } from "../../providers/onboarding-provider";
import { useAuth0 } from "@auth0/auth0-react";
import {
  IneligibleConfirmationModal,
  wouldAnswerBeIneligible,
} from "../../components/onboarding/IneligibleConfirmationModal";
import { FormShell } from "../../components/onyx/FormShell";
import { FormActions } from "../../components/onyx/Field";
import { Button } from "../../components/onyx/Button";
import { 
  getPreScreenNextQuestion, 
  getPreScreenPreviousQuestion, 
  getPreScreenCurrentAnswer, 
  submitPreScreenAnswer,
  getPreScreenNextQuestionAnonymous,
  submitPreScreenAnswerAnonymous,
  getPreScreenCurrentAnswerAnonymous,
  getPreScreenPreviousQuestionAnonymous,
  transferSessionAnswers,
  getQuestionGroupBySlug,
  getDefaultQuestionGroup,
} from "../../services/backend.service";
import { callWithAuthRetry } from "../../services/auth.service";
import { PageLoader } from "../../components/page-loader";
import analyticsService from "../../services/analytics.service";
import QuestionRenderer from "../../components/onboarding/QuestionRenderer";
export const PreScreenPage = () => {
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const { onboardingData, refreshOnboardingData, completeStep, navigateToLatestStep, fetchOnboardingStatus } = useOnboarding();
  const [searchParams] = useSearchParams();
  const { study } = useParams();



  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [originalAnswer, setOriginalAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentAnswerId, setCurrentAnswerId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isIneligible, setIsIneligible] = useState(() => {
    // Initialize from localStorage to persist across re-renders
    return localStorage.getItem('user_determined_ineligible') === 'true';
  });
  const transferAttemptedRef = useRef(false); // Track if transfer has been attempted

  // Session management for anonymous users
  const [sessionKey, setSessionKey] = useState(null);
  const [questionGroup, setQuestionGroup] = useState(null); // Initialize as null to prevent premature API calls
  const [isAnonymous, setIsAnonymous] = useState(!isAuthenticated);
  const [isTransferringSession, setIsTransferringSession] = useState(false);
  const ftAttributionRef = useRef(null);

  const readFtCookies = () => {
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const [key, ...rest] = c.trim().split('=');
      if (key.startsWith('ft_')) {
        try {
          acc[key] = decodeURIComponent(rest.join('='));
        } catch (e) {
          acc[key] = rest.join('=');
        }
      }
      return acc;
    }, {});
    return Object.keys(cookies).length > 0 ? cookies : null;
  };

  // Initialize session and determine mode (authenticated vs anonymous)
  useEffect(() => {
    const initializeSession = async () => {
      if (authLoading) return;
      
      // Set authentication mode
      setIsAnonymous(!isAuthenticated);
      
      // Check if user is determined ineligible from previous session
      if (localStorage.getItem('user_determined_ineligible') === 'true') {
        setIsIneligible(true);
        setSurveyCompleted(true);
        setCurrentQuestion(null);
        setCurrentAnswer(null);
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        // Anonymous mode - get or create session
        const storedSessionKey = localStorage.getItem('prescreen_session_key');
        const studyParam = study || searchParams.get('study');
        
        if (storedSessionKey) {
          // Existing session - restore session and question group
          setSessionKey(storedSessionKey);
          const storedQuestionGroup = localStorage.getItem('prescreen_question_group');
          if (storedQuestionGroup) {
            setQuestionGroup(storedQuestionGroup);
          } else if (studyParam) {
            // Restore question group from URL parameter if not stored
            try {
              const { data, error } = await getQuestionGroupBySlug(studyParam);
              if (data?.question_group) {
                setQuestionGroup(data.question_group.name);
                localStorage.setItem('prescreen_question_group', data.question_group.name);
              } else {
                // Use default question group instead of hardcoded value
                const defaultResponse = await getDefaultQuestionGroup();
                const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
                setQuestionGroup(defaultGroupName);
                localStorage.setItem('prescreen_question_group', defaultGroupName);
              }
            } catch (err) {
              // Use default question group instead of hardcoded value
              const defaultResponse = await getDefaultQuestionGroup();
              const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
              setQuestionGroup(defaultGroupName);
              localStorage.setItem('prescreen_question_group', defaultGroupName);
            }
          } else {
            // Use default question group instead of hardcoded value
            const defaultResponse = await getDefaultQuestionGroup();
            const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
            setQuestionGroup(defaultGroupName);
            localStorage.setItem('prescreen_question_group', defaultGroupName);
          }
        } else {
          // New session - generate session key and determine question group
          const newSessionKey = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          setSessionKey(newSessionKey);
          localStorage.setItem('prescreen_session_key', newSessionKey);
          ftAttributionRef.current = readFtCookies();
          
          if (studyParam) {
            try {
              const { data, error } = await getQuestionGroupBySlug(studyParam);
              if (data?.question_group) {
                setQuestionGroup(data.question_group.name);
                localStorage.setItem('prescreen_question_group', data.question_group.name);
              } else {
                console.warn(`Question group not found for slug: ${studyParam}, falling back to default`);
                // Use default question group instead of hardcoded value
                const defaultResponse = await getDefaultQuestionGroup();
                const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
                setQuestionGroup(defaultGroupName);
                localStorage.setItem('prescreen_question_group', defaultGroupName);
              }
            } catch (err) {
              console.error('Error fetching question group:', err);
              // Use default question group instead of hardcoded value
              const defaultResponse = await getDefaultQuestionGroup();
              const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
              setQuestionGroup(defaultGroupName);
              localStorage.setItem('prescreen_question_group', defaultGroupName);
            }
          } else {
            // Use default question group instead of hardcoded value
            const defaultResponse = await getDefaultQuestionGroup();
            const defaultGroupName = defaultResponse.data?.question_group?.name || 'study_selector';
            setQuestionGroup(defaultGroupName);
            localStorage.setItem('prescreen_question_group', defaultGroupName);
          }
        }
      } else {
        // Authenticated mode - resolve question group
        // IMPORTANT: Check if we just completed session transfer and preserve the current question group
        const justCompletedTransfer = transferAttemptedRef.current && !localStorage.getItem('transfer_session_key');
        
        if (justCompletedTransfer && questionGroup) {
          // We just completed session transfer and already have the right question group
          // Don't override it - just ensure it's stored for future sessions
          localStorage.setItem('authenticated_prescreen_question_group', questionGroup);
          return;
        }
        
        // Normal authenticated initialization
        const storedQuestionGroup = localStorage.getItem('transfer_question_group');
        const authenticatedQuestionGroup = localStorage.getItem('authenticated_prescreen_question_group');
        const studyParam = study || searchParams.get('study');
        
        if (storedQuestionGroup) {
          // User just logged in and we have a stored question group from anonymous session
          setQuestionGroup(storedQuestionGroup);
        } else if (authenticatedQuestionGroup) {
          // User is returning to an authenticated session and we have their question group stored
          setQuestionGroup(authenticatedQuestionGroup);
        } else if (studyParam) {
          try {
            const { data, error } = await getQuestionGroupBySlug(studyParam);
            if (data?.question_group) {
              setQuestionGroup(data.question_group.name);
            } else {
              console.warn(`Question group not found for slug: ${studyParam}, falling back to default`);
              // Fall back to default question group
              const defaultResponse = await getDefaultQuestionGroup();
              if (defaultResponse.data?.question_group) {
                setQuestionGroup(defaultResponse.data.question_group.name);
              } else {
                console.error('Could not fetch default question group');
                setQuestionGroup('study_selector'); // Last resort fallback
              }
            }
          } catch (err) {
            console.error('Error fetching question group:', err);
            // Fall back to default question group
            try {
              const defaultResponse = await getDefaultQuestionGroup();
              if (defaultResponse.data?.question_group) {
                setQuestionGroup(defaultResponse.data.question_group.name);
              } else {
                setQuestionGroup('study_selector'); // Last resort fallback
              }
                          } catch (defaultErr) {
                console.error('Error fetching default question group:', defaultErr);
                setQuestionGroup('study_selector'); // Last resort fallback
              }
          }
        } else {
          // No study parameter and no stored question group - use default question group
          try {
            const { data, error } = await getDefaultQuestionGroup();
            if (data?.question_group) {
              setQuestionGroup(data.question_group.name);
            } else {
              console.error('Could not fetch default question group');
              setQuestionGroup('study_selector'); // Last resort fallback
            }
          } catch (err) {
            console.error('Error fetching default question group:', err);
            setQuestionGroup('study_selector'); // Last resort fallback
          }
        }
      }
    };
    
    initializeSession();
  }, [authLoading, isAuthenticated, study, searchParams]);

    useEffect(() => {
    // Only start fetching questions after session is initialized AND question group is resolved
    // Also prevent fetching during session transfer to avoid race conditions
    
    // If user is determined ineligible, stop loading immediately
    if (isIneligible || localStorage.getItem('user_determined_ineligible') === 'true') {
      setLoading(false);
      return;
    }
    
    // If user just authenticated and there's a pending session transfer, wait for it to complete
    const hasPendingTransfer = isAuthenticated && localStorage.getItem('transfer_session_key');
    if (hasPendingTransfer && !isTransferringSession) {
      // Don't fetch questions yet - wait for session transfer to complete
      return;
    }
    
    if (questionGroup && !isTransferringSession && ((isAnonymous && sessionKey) || (!isAnonymous && isAuthenticated))) {
      // Track pre-screen start when component mounts
      analyticsService.trackOnboarding('pre_screen_started', 'page_visit', {
        mode: isAnonymous ? 'anonymous' : 'authenticated',
        study: questionGroup
      });
      
      // Only fetch next question if survey hasn't been completed
      if (!surveyCompleted) {
        fetchNextQuestion();
      }
    }
  }, [sessionKey, isAnonymous, isAuthenticated, surveyCompleted, questionGroup, isTransferringSession, isIneligible]);

  // Helper function to determine if Next button should be disabled
  const isNextButtonDisabled = () => {
    // Debug logging
    console.log('isNextButtonDisabled check:', {
      currentQuestion: currentQuestion?.text,
      required: currentQuestion?.required,
      currentAnswer,
      currentAnswerType: typeof currentAnswer,
      submitting,
      surveyCompleted,
      validationError
    });

    if (submitting || surveyCompleted || validationError) {
      return true;
    }
    
    // If question is required, disable button when no answer is provided
    if (currentQuestion && currentQuestion.required) {
      // Handle null, undefined, and empty string as "no answer"
      const hasNoAnswer = currentAnswer === null || 
                         currentAnswer === undefined || 
                         currentAnswer === '';
      return hasNoAnswer;
    }
    
    // If question is optional, button is always enabled (user can skip)
    return false;
  };

  // Client-side validation function
  const validateAnswer = (question, value) => {
    // For required fields, check if value is null/undefined (no selection made)
    // For boolean questions, false is a valid selection
    if (question.required && (value === null || value === undefined)) {
      return 'This field is required';
    }
    
    // If no value provided and field is optional, that's fine
    if ((value === null || value === undefined) && !question.required) {
      return null;
    }
    
    switch (question.question_type) {
      case 'phone_number':
        const digits = value.replace(/\D/g, '');
        if (digits.length !== 10) {
          return 'Phone number must be exactly 10 digits';
        }
        break;
      case 'email':
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailPattern.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'number':
        if (isNaN(value) || value === '') {
          return 'Please enter a valid number';
        }
        break;
    }
    
    return null;
  };

  const fetchNextQuestion = async (answerId = null) => {
    try {
      setLoading(true);
      setValidationError(null);
      
      let data, error;
      
      if (isAnonymous) {
        const response = await getPreScreenNextQuestionAnonymous(sessionKey, answerId, questionGroup);
        data = response.data;
        error = response.error;
      } else {
        const response = await callWithAuthRetry(
          getAccessTokenSilently,
          (token) => getPreScreenNextQuestion(token, answerId)
        );
        data = response.data;
        error = response.error;
      }

      if (error) {
        console.error('fetchNextQuestion error:', error);
        setError(error);
        return;
      }

      if (data?.status === 'error') {
        // Handle ineligibility gracefully without setting error state
        if (data.message === 'You are not eligible for this study') {
          // Track ineligibility
          analyticsService.trackOnboarding('pre_screen_ineligible', 'study_rejection');
          if (!isAnonymous) {
            // Authenticated user - show ineligible screen immediately
            setIsIneligible(true);
            setSurveyCompleted(true);
            setCurrentQuestion(null);
            setCurrentAnswer(null);
            await completeStep('INELIGIBLE');
          } else {
            // For anonymous users, still show login prompt to capture contact info
            // Set ineligible flag so we can show ineligible screen after login
            localStorage.setItem('prescreen_ineligible', 'true');
            setSurveyCompleted(true);
            setShowLoginPrompt(true);
            setCurrentQuestion(null);
            setCurrentAnswer(null);
          }
          return;
        }
        
        // For other errors, set error state
        setError(data.message);
        return;
      }

      // Check if the flow is already completed (for session restoration)
      if (data?.flow_completed && data?.requires_login) {
        setSurveyCompleted(true);
        setShowLoginPrompt(true);
        setCurrentQuestion(null);
        setCurrentAnswer(null);
        analyticsService.trackOnboarding('pre_screen_completed', 'requires_login');
        return;
      }

      if (data?.next_question) {
        setCurrentQuestion(data.next_question);
        
        // With the new approach, backend creates an empty answer and returns its ID
        if (data.answer_id) {
          setCurrentAnswerId(data.answer_id);
          
          // For new questions, the answer will be null/empty initially
          // We'll fetch the actual answer value to see if it was already filled
          let answerData;
          if (isAnonymous) {
            const response = await getPreScreenCurrentAnswerAnonymous(sessionKey, data.next_question.id);
            answerData = response.data;
          } else {
            const token = await getAccessTokenSilently();
            const response = await getPreScreenCurrentAnswer(token, data.next_question.id);
            answerData = response.data;
          }
          
          const existingAnswer = answerData?.answer?.answer_value ?? null;
          // Treat empty strings as null for consistency
          const cleanAnswer = existingAnswer === '' ? null : existingAnswer;
          setCurrentAnswer(cleanAnswer);
          setOriginalAnswer(cleanAnswer);
        } else {
          // Fallback if no answer_id provided (shouldn't happen with new approach)
          setCurrentAnswer(null);
          setOriginalAnswer(null);
          setCurrentAnswerId(null);
        }
      } else {
        // No more questions
        if (isAnonymous) {
          // Anonymous user completed - show login prompt
          if (data?.flow_completed && data?.requires_login) {
            setSurveyCompleted(true);
            setShowLoginPrompt(true);
            setCurrentQuestion(null); // Important: Clear current question immediately
            setCurrentAnswer(null);   // Important: Clear current answer immediately
            analyticsService.trackOnboarding('pre_screen_completed', 'requires_login');
            return;
          }
        } else {
          // Authenticated user flow
          if (data?.redirect_to_approval) {
            // Skip completion screen and go directly to approval
            setSurveyCompleted(true);
            setCurrentQuestion(null); // Important: Clear current question immediately
            
            // Clean up stored question group since survey is completed
            localStorage.removeItem('authenticated_prescreen_question_group');
            
            analyticsService.trackOnboarding('pre_screen_completed', 'redirect_to_approval');
            await fetchOnboardingStatus();
            navigate('/pending-approval');
            return;
          }
        }
        
        // Auto-advance authenticated users, show completion screen for anonymous users
        if (!isAnonymous) {
          // Authenticated user - auto-advance to next step
          setCurrentQuestion(null);  // Important: Clear current question immediately
          setCurrentAnswer(null);    // Important: Clear current answer immediately
          setOriginalAnswer(null);
          setCurrentAnswerId(null);
          
          // Clean up stored question group since survey is completed
          localStorage.removeItem('authenticated_prescreen_question_group');
          
          analyticsService.trackOnboarding('pre_screen_completed', 'auto_advance');
          
          // Auto-advance to next onboarding step
          await completeStep('PRE_SCREEN');
          // The onboarding provider will handle navigation to the next step
        } else {
          // Anonymous user - show completion screen
          setSurveyCompleted(true);
          setCurrentQuestion(null);  // Important: Clear current question immediately
          setCurrentAnswer(null);    // Important: Clear current answer immediately
          setOriginalAnswer(null);
          setCurrentAnswerId(null);
          
          // Clean up stored question group since survey is completed
          localStorage.removeItem('authenticated_prescreen_question_group');
          
          analyticsService.trackOnboarding('pre_screen_completed', 'survey_finished');
        }
      }
    } catch (err) {
      console.error('fetchNextQuestion catch error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousQuestion = async () => {
    try {
      setLoading(true);
      setValidationError(null);
      
      // Use current answer ID, or "last" if no current answer (e.g., on completion screen)
      const answerIdToUse = currentAnswerId || "last";
      
      let data, error;
      
      if (isAnonymous) {
        const response = await getPreScreenPreviousQuestionAnonymous(sessionKey, answerIdToUse);
        data = response.data;
        error = response.error;
      } else {
        const response = await callWithAuthRetry(
          getAccessTokenSilently,
          (token) => getPreScreenPreviousQuestion(token, answerIdToUse)
        );
        data = response.data;
        error = response.error;
      }

      if (error) {
        setError(error);
        return;
      }

      if (data?.previous_question) {
        setCurrentQuestion(data.previous_question);
        
        // The backend already provides the answer_id for the previous question
        if (data.answer_id) {
          setCurrentAnswerId(data.answer_id);
          
          // Fetch the actual answer value for this answer_id
          if (isAnonymous) {
            const response = await getPreScreenCurrentAnswerAnonymous(sessionKey, data.previous_question.id);
            const answerData = response.data;
            if (answerData?.answer?.answer_value !== undefined) {
              setCurrentAnswer(answerData.answer.answer_value);
              setOriginalAnswer(answerData.answer.answer_value);
            } else {
              setCurrentAnswer(null);
              setOriginalAnswer(null);
            }
          } else {
            const token = await getAccessTokenSilently();
            const { data: answerData } = await getPreScreenCurrentAnswer(token, data.previous_question.id);
            if (answerData?.answer?.answer_value !== undefined) {
              setCurrentAnswer(answerData.answer.answer_value);
              setOriginalAnswer(answerData.answer.answer_value);
            } else {
              setCurrentAnswer(null);
              setOriginalAnswer(null);
            }
          }
        } else {
          // No previous answer (shouldn't happen with new approach)
          setCurrentAnswer(null);
          setOriginalAnswer(null);
          setCurrentAnswerId(null);
        }
      } else {
        // No previous question - user is at the beginning
        console.log('At the beginning of the survey - no previous question');
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value) => {
    setCurrentAnswer(value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if survey is already completed or currently submitting
    if (surveyCompleted || submitting || currentAnswer === null) {
      return;
    }
    
    // Client-side validation
    const validationErr = validateAnswer(currentQuestion, currentAnswer);
    if (validationErr) {
      setValidationError(validationErr);
      return;
    }
    
    // Client-side ineligible-routing check via the shared helper —
    // identical predicate as the clinic-side QuestionnaireStepPanel
    // and the backend's QuestionFlowTraverser. Pop the modal and
    // bail; the actual submit fires on confirm.
    if (wouldAnswerBeIneligible(currentQuestion, currentAnswer)) {
      setShowConfirmModal(true);
      return;
    }

    await submitAnswer();
  };

  // Add new function to handle confirmed submission
  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    await submitAnswer(pendingAnswer);
  };

  // Handle login flow for anonymous users
  const handleLogin = async () => {
    try {
      // Store session key in localStorage for retrieval after login
      if (sessionKey) {
        localStorage.setItem('transfer_session_key', sessionKey);
        localStorage.setItem('transfer_question_group', questionGroup);
      }
      
      // Analytics tracking
      analyticsService.trackOnboarding('pre_screen_login_initiated', 'login_redirect');
      
      // Redirect to login with post-login redirect back to pre-screen
      // The onboardingProvider will handle navigation to the proper next step after session transfer
      await loginWithRedirect({
        appState: {
          returnTo: '/pre-screen'
        },
        authorizationParams: {
          screen_hint: "signup",
        },
      });
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to initiate login. Please try again.');
    }
  };

  // Transfer session answers after authentication (called in auth callback)
  const transferSessionData = async () => {
    try {
      const transferSessionKey = localStorage.getItem('transfer_session_key');
      console.log('📤 PreScreenPage: transferSessionData called, transferSessionKey:', transferSessionKey);
      if (!transferSessionKey) return;
      
      const transferQuestionGroup = localStorage.getItem('transfer_question_group');
      setIsTransferringSession(true);
      
      // Use callWithAuthRetry to handle race condition where Post-User-Registration
      // Action hasn't completed yet (user just signed up, token missing org context)
      console.log('📤 PreScreenPage: Calling transferSessionAnswers with retry...');
      const { data, error } = await callWithAuthRetry(
        getAccessTokenSilently,
        (token) => transferSessionAnswers(token, transferSessionKey, transferQuestionGroup)
      );
      console.log('📤 PreScreenPage: transferSessionAnswers result:', { hasData: !!data, hasError: !!error, errorStatus: error?.status });
      
      if (error) {
        console.error('Failed to transfer session data:', error);
        setError('Failed to transfer your answers. Please try again.');
        return;
      }
      
      // Check if user was ineligible during anonymous flow OR backend determined they're ineligible
      const wasIneligibleFromLocalStorage = localStorage.getItem('prescreen_ineligible') === 'true';
      const wasIneligibleFromBackend = data?.is_ineligible === true || data?.next_step === 'INELIGIBLE';
      const wasIneligible = wasIneligibleFromLocalStorage || wasIneligibleFromBackend;
      

      
        // Clear the stored session keys (but preserve ineligible flag)
  localStorage.removeItem('transfer_session_key');
  localStorage.removeItem('transfer_question_group');
  localStorage.removeItem('prescreen_session_key');
  localStorage.removeItem('prescreen_ineligible');
  // Note: Don't remove 'user_determined_ineligible' here as it should persist for this session
      
      if (wasIneligible) {
        // User was ineligible - show ineligible screen after capturing their info
        localStorage.setItem('user_determined_ineligible', 'true'); // Mark as ineligible permanently
        setIsIneligible(true);
        setSurveyCompleted(true);
        setCurrentQuestion(null);
        setCurrentAnswer(null);
        await completeStep('INELIGIBLE');
        return;
      }
      
      // Handle different transfer statuses
      if (data?.status === 'already_enrolled') {
        // User is already enrolled - show a friendly message and redirect
        analyticsService.trackOnboarding('pre_screen_already_enrolled', 'duplicate_enrollment');
        
        // Refresh onboarding status and navigate to latest step
        await fetchOnboardingStatus();
        navigateToLatestStep();
        return;
      }
      
      // Normal successful transfer
      analyticsService.trackOnboarding('pre_screen_session_transferred', 'data_migration', {
        transferred_answers: data?.transferred_answers,
        next_step: data?.next_step,
        session_was_completed: data?.session_was_completed
      });
      
      // Check if the session was completed before transfer
      if (data?.session_was_completed) {
        // Session was completed - user finished the survey, navigate to next step
        
        // Clean up stored question group since survey is completed
        localStorage.removeItem('authenticated_prescreen_question_group');
        
        await fetchOnboardingStatus();
        navigateToLatestStep();
      } else {
        // Session was incomplete - user was in the middle of the survey
        // Stay on pre-screen page and continue where they left off
        
        // IMPORTANT: Store the question group BEFORE switching modes to ensure it's preserved
        localStorage.setItem('authenticated_prescreen_question_group', questionGroup);
        
        setIsAnonymous(false); // Switch to authenticated mode
        setSessionKey(null);   // Clear session key
        setSurveyCompleted(false); // Ensure survey is not marked as completed
        setShowLoginPrompt(false); // Hide login prompt
        
        // Refresh onboarding status but don't navigate away
        await fetchOnboardingStatus();
        
        // Important: Maintain the current question group state during transition
        // This ensures the useEffect will use the correct question group when it re-runs
      }
      
      // Clear the stored session keys (but preserve authenticated_prescreen_question_group)
      localStorage.removeItem('transfer_session_key');
      localStorage.removeItem('transfer_question_group');
      localStorage.removeItem('prescreen_session_key');
      localStorage.removeItem('prescreen_ineligible');
      // Note: Don't remove 'user_determined_ineligible' or 'authenticated_prescreen_question_group' here
      
    } catch (error) {
      console.error('Transfer session data error:', error);
      setError('Failed to transfer your answers. Please try again.');
    } finally {
      setIsTransferringSession(false);
    }
  };

  // Auto-transfer on mount if user just authenticated
  useEffect(() => {
    const transferSessionKey = localStorage.getItem('transfer_session_key');
    const prescreenSessionKey = localStorage.getItem('prescreen_session_key');
    const prescreenQuestionGroup = localStorage.getItem('prescreen_question_group');
    
    if (isAuthenticated && !authLoading && !transferAttemptedRef.current) {
      // Case 1: Normal transfer flow (user completed anonymous survey and clicked login)
      if (transferSessionKey) {
        transferAttemptedRef.current = true;
        transferSessionData();
      }
      // Case 2: Mid-survey login (user logged in while still in anonymous survey)
      else if (prescreenSessionKey && prescreenQuestionGroup) {
        // Convert the anonymous session data to transfer format
        localStorage.setItem('transfer_session_key', prescreenSessionKey);
        localStorage.setItem('transfer_question_group', prescreenQuestionGroup);
        
        transferAttemptedRef.current = true;
        transferSessionData();
      }
    }
  }, [isAuthenticated, authLoading]);

  const submitAnswer = async (confirmedAnswer = null) => {
    try {
      setSubmitting(true);
      const answerToSubmit = confirmedAnswer ?? currentAnswer;
      
      let data, error;
      
      if (isAnonymous) {
        const response = await submitPreScreenAnswerAnonymous(
          sessionKey,
          currentQuestion.id,
          answerToSubmit,
          questionGroup,
          ftAttributionRef.current
        );
        data = response.data;
        error = response.error;
      } else {
        const response = await callWithAuthRetry(
          getAccessTokenSilently,
          (token) => submitPreScreenAnswer(
            token,
            currentQuestion.id,
            answerToSubmit,
            currentAnswerId
          )
        );
        data = response.data;
        error = response.error;
      }

      if (error) {
        console.error('Submit answer error:', error);
        setError(error);
        // Track form submission error
        analyticsService.trackFormSubmission('pre_screen_question', false);
        return;
      }

      // Track successful question submission
      analyticsService.trackFormSubmission('pre_screen_question', true);
      await fetchNextQuestion(data?.answer?.id);
    } catch (err) {
      console.error('submitAnswer catch error:', err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <PageLayout>
        <div className="content-layout">
          <OnboardingProgressBar />
          <PageLoader />
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="content-layout">
          <OnboardingProgressBar />
          <FormShell>
            <h1 id="page-title" className="content__title">
              Error
            </h1>
            <p className="content__body">
              {error.message || "An error occurred while loading the pre-screen questions."}
            </p>
            <FormActions>
              <Button type="button" variant="primary" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </FormActions>
          </FormShell>
        </div>
      </PageLayout>
    );
  }

  const renderConfirmationModal = () => (
    <IneligibleConfirmationModal
      open={showConfirmModal}
      onCancel={() => setShowConfirmModal(false)}
      onConfirm={handleConfirmedSubmit}
      message="Your answer indicates you may not be eligible for this study. Are you sure you want to submit this answer?"
    />
  );

  return (
    <PageLayout>
      <div className="content-layout">
        <OnboardingProgressBar />
        <FormShell>

          {currentQuestion ? (
            <form onSubmit={handleSubmit}>
                <QuestionRenderer
                  question={currentQuestion}
                  value={currentAnswer}
                  onChange={handleChange}
                  onValidationError={setValidationError}
                  disabled={submitting}
                />
                {validationError && (
                  <div className="onyx-field-error-inline">
                    {validationError}
                  </div>
                )}
                
                <FormActions>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={fetchPreviousQuestion}
                  >
                    Previous
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isNextButtonDisabled()}
                    title={isNextButtonDisabled() && currentQuestion?.required ? 'Please answer the required question' : ''}
                  >
                    {submitting ? 'Submitting...' : 'Next'}
                  </Button>
                </FormActions>
                </form>
          ) : (
            <>
              {(isIneligible || localStorage.getItem('user_determined_ineligible') === 'true') ? (
                // User found ineligible
                <>
                  <h1 id="page-title" className="content__title">
                    Not Eligible
                  </h1>
                  <p className="content__body">
                    Based on your answers, you are not eligible for this study at this time. 
                    Thank you for your interest in our research.
                  </p>
                  <FormActions align="center">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        // Clear ineligible flag so user can try again in future
                        localStorage.removeItem('user_determined_ineligible');
                        navigate('/');
                      }}
                    >
                      Return to Home
                    </Button>
                  </FormActions>
                </>
              ) : showLoginPrompt ? (
                // Anonymous user completed - show login prompt
                <>
                  <p className="content__body">
                    Please sign up or login to submit and track your application.
                  </p>
                  <FormActions align="center">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleLogin}
                    >
                      Sign up or Login
                    </Button>
                  </FormActions>
                </>
              ) : (
                // Authenticated user or other completion scenarios
                <>
                  <p className="content__body">
                    Thanks for answering those questions. Let's keep going!
                  </p>
                  <FormActions>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={fetchPreviousQuestion}
                    >
                      Review Previous
                    </Button>
                    {!isAnonymous && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={async () => {
                          await completeStep('PRE_SCREEN');
                          // The onboarding provider will handle navigation to the next step
                        }}
                      >
                        Continue
                      </Button>
                    )}
                  </FormActions>
                </>
              )}
            </>
          )}
        </FormShell>
      </div>
      {renderConfirmationModal()}
    </PageLayout>
  );
};
